export class SessionStatus {
  static SCHEDULED = "scheduled";
  static COMPLETED = "completed";
  static INSTRUCTOR_DELETED = "instructor-deleted";
  static STUDENT_DELETED = "student-deleted";
  static ADMIN_DELETED = "admin-deleted";
  static INSTRUCTOR_CANCELED = "instructor-canceled";
  static STUDENT_CANCELED = "student-canceled";
  static ISSUES_RESCHEDULED = "issues-rescheduled";
  static INSTRUCTOR_MISSED = "instructor-missed";
  static STUDENT_MISSED = "student-missed";
  static IN_PROGRESS = "in-progress";
}

export class StudentBillingType {
  static CHARGED = "charged";
  static NOT_CHARGED = "not-charged";
}

export class InstructorBillingType {
  static PAID = "paid";
  static NOT_PAID = "not-paid";
}

export class SessionStatusDisplayName {
  static SCHEDULED = "Scheduled";
  static COMPLETED = "Completed";
  static INSTRUCTOR_DELETED = "Instructor Deleted";
  static STUDENT_DELETED = "Student Deleted";
  static ADMIN_DELETED = "Admin Deleted";
  static INSTRUCTOR_CANCELED = "Instructor Cancelled within 24 hours";
  static STUDENT_CANCELED = "Student Cancelled within 24 hours";
  static INSTRUCTOR_RESCHEDULED = "Instructor Rescheduled within 24 hours";
  static STUDENT_RESCHEDULED = "Student Rescheduled within 24 hours";
  static ISSUES_RESCHEDULED = "Technical Difficulties";
  static INSTRUCTOR_MISSED = "Instructor Missed";
  static STUDENT_MISSED = "Student Missed";
  static IN_PROGRESS = "In Progress";
}

export function getStatusDisplayName(session) {
  switch (session.status) {
    case SessionStatus.SCHEDULED:
      return SessionStatusDisplayName.SCHEDULED;
    case SessionStatus.COMPLETED:
      return SessionStatusDisplayName.COMPLETED;
    case SessionStatus.INSTRUCTOR_DELETED:
      return SessionStatusDisplayName.INSTRUCTOR_DELETED;
    case SessionStatus.STUDENT_DELETED:
      return SessionStatusDisplayName.STUDENT_DELETED;
    case SessionStatus.ADMIN_DELETED:
      return SessionStatusDisplayName.ADMIN_DELETED;
    case SessionStatus.INSTRUCTOR_CANCELED:
      if (isInstructorRescheduled(session)) {
        return SessionStatusDisplayName.INSTRUCTOR_RESCHEDULED;
      }
      return SessionStatusDisplayName.INSTRUCTOR_CANCELED;
    case SessionStatus.STUDENT_CANCELED:
      if (isStudentRescheduled(session)) {
        return SessionStatusDisplayName.STUDENT_RESCHEDULED;
      }
      return SessionStatusDisplayName.STUDENT_CANCELED;
    case SessionStatus.ISSUES_RESCHEDULED:
      return SessionStatusDisplayName.ISSUES_RESCHEDULED;
    case SessionStatus.INSTRUCTOR_MISSED:
      return SessionStatusDisplayName.INSTRUCTOR_MISSED;
    case SessionStatus.STUDENT_MISSED:
      return SessionStatusDisplayName.STUDENT_MISSED;
    case SessionStatus.IN_PROGRESS:
      return SessionStatusDisplayName.IN_PROGRESS;
    default:
      return session.status;
  }
}

export function isScheduled(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.SCHEDULED;
}

export function isCompleted(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.COMPLETED;
}

export function isDeleted(status) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.INSTRUCTOR_DELETED ||
    status === SessionStatus.STUDENT_DELETED ||
    status === SessionStatus.ADMIN_DELETED
  );
}

export function isCanceled(status) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.INSTRUCTOR_CANCELED ||
    status === SessionStatus.STUDENT_CANCELED
  );
}

export function isIssuesRescheduled(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.ISSUES_RESCHEDULED;
}

export function isMissed(status) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.INSTRUCTOR_MISSED || status === SessionStatus.STUDENT_MISSED
  );
}

export function isInstructorCanceled(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.INSTRUCTOR_CANCELED;
}

export function isInstructorRescheduled(session) {
  if (!session) {
    return false;
  }
  return isInstructorCanceled(session.status) && session.rescheduled_session_id;
}

export function isInstructorMissed(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.INSTRUCTOR_MISSED;
}

export function isStudentCanceled(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.STUDENT_CANCELED;
}

export function isStudentRescheduled(session) {
  if (!session) {
    return false;
  }
  return isStudentCanceled(session.status) && session.rescheduled_session_id;
}

export function isStudentMissed(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.STUDENT_MISSED;
}

export function isInProgress(status) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.IN_PROGRESS;
}

export function getAllStatuses() {
  return [
    SessionStatus.SCHEDULED,
    SessionStatus.COMPLETED,
    SessionStatus.INSTRUCTOR_DELETED,
    SessionStatus.STUDENT_DELETED,
    SessionStatus.ADMIN_DELETED,
    SessionStatus.INSTRUCTOR_CANCELED,
    SessionStatus.STUDENT_CANCELED,
    SessionStatus.ISSUES_RESCHEDULED,
    SessionStatus.INSTRUCTOR_MISSED,
    SessionStatus.STUDENT_MISSED,
    SessionStatus.IN_PROGRESS,
  ];
}

export function isLessonFinishedOrInProgress(status) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.COMPLETED ||
    status === SessionStatus.IN_PROGRESS ||
    status === SessionStatus.ISSUES_RESCHEDULED ||
    status === SessionStatus.INSTRUCTOR_MISSED ||
    status === SessionStatus.STUDENT_MISSED
  );
}

export function isLessonStatusWithReason(status) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.STUDENT_MISSED || status === SessionStatus.STUDENT_CANCELED
  );
}

export function getStudentBillingType(session) {
  if (!session) {
    return null;
  }
  const status = session.status;
  if (
    isScheduled(status) ||
    isCompleted(status) ||
    isStudentMissed(status) ||
    isStudentCanceled(status) ||
    isInProgress(status)
  ) {
    return StudentBillingType.CHARGED;
  } else {
    return StudentBillingType.NOT_CHARGED;
  }
}

export function getInstructorBillingType(session) {
  if (!session) {
    return null;
  }
  const status = session.status;
  if (isInstructorMissed(status) || isInstructorCanceled(status) || isDeleted(status)) {
    return InstructorBillingType.NOT_PAID;
  } else {
    return InstructorBillingType.PAID;
  }
}
